import _ from "lodash";
import { Period, Utils } from "revlock-webutils";
import RevenueArrangementUtils from "./RevenueArrangementUtils";
import * as ExpenseArrangementHelper from "../ExpenseArrangementHelper";
import { RecognitionRule, ModificationType } from "../Reference";
import ItemType from "../reference/ItemType";

export default class ProspectiveModificationHelper {
    /**
     * Applies Prospective Modification to the sales order using the previous revenueArrangement
     * @param {*} salesOrder the sales order to apply modification
     * @param {*} revenueArrangement the new revenueArrangement
     * @param {*} previousRevenueArrangement the previous revenueArrangement
     * @param {*} orgConfig
     * @param {*} orgConfigByKey
     * @param {*} calendarConfig
     */
    static applyProspectiveModification(
        salesOrder,
        revenueArrangement,
        previousRevenueArrangement,
        orgConfig,
        orgConfigByKey,
        calendarConfig
    ) {
        let revenueArrangementItemsToPush = [];

        const endDateInclusiveConfig = orgConfig.find(
            (config) => config.id === "properties/ratable_end_date_inclusive"
        );
        const endDateInclusive =
            endDateInclusiveConfig && endDateInclusiveConfig.value;

        const isAfter = endDateInclusive ? Utils.isSameOrAfter : Utils.isAfter;

        const notEmpty = (value) => !_.isEmpty(value);

        const distributeExcessRevenue = (isReprocess) => {
            const orderTCV = salesOrder.salesOrderItem.reduce(
                (total, item) => total + item.salePrice * item.quantity,
                0
            );
            const totalProductRevenue =
                revenueArrangement.revenueArrangementItem.reduce(
                    (total, item) => {
                        const { salesOrderItem } = item;
                        const extendedSalePrice =
                            salesOrderItem.salePrice * salesOrderItem.quantity;
                        return (
                            total +
                            (item.unrecognizedRevenue ??
                                item.productRevenue ??
                                extendedSalePrice)
                        );
                    },
                    0
                );
            const excessRevenue = orderTCV - totalProductRevenue;
            if (Utils.approxeq(excessRevenue, 0)) {
                return;
            }

            const hasTerminatedItem = salesOrder.salesOrderItem.some((item) =>
                Utils.isSame(item.endDate, salesOrder.modificationDate)
            );
            if (hasTerminatedItem) {
                const unrecognizedItems =
                    revenueArrangement.revenueArrangementItem.filter(
                        (item) => !ItemType.isFrozen(item.itemType)
                    );
                if (unrecognizedItems.length > 0) {
                    // split equally between the unrecognized items
                    const adjustmentPerItem =
                        excessRevenue / unrecognizedItems.length;
                    unrecognizedItems.forEach((item) => {
                        if (item.itemType === ItemType.UNCHANGED) {
                            item.unrecognizedRevenue =
                                item.salesOrderItem.extendedSalePrice +
                                adjustmentPerItem;
                        } else {
                            const adjustment = isReprocess
                                ? 0
                                : adjustmentPerItem;
                            item.unrecognizedRevenue =
                                (item.unrecognizedRevenue ?? 0) + adjustment;
                        }
                    });
                } else if (orderTCV === 0) {
                    // write-off scenario, update the frozen items plan to 0
                    revenueArrangement.revenueArrangementItem
                        .filter((item) => ItemType.isFrozen(item.itemType))
                        .forEach((item) => {
                            item.plan?.forEach((plan) => {
                                plan.planAmount = 0;
                            });
                            item.productRevenue = 0;
                        });
                }
            }
        };

        const getModificationPeriodPlan = (item, plan, modificationPeriod) => {
            let modPeriodPlans = plan.filter(
                (p) => p.actgPeriod.period == modificationPeriod.period
            );
            let modPeriodPlan;
            if (modPeriodPlans && modPeriodPlans.length) {
                modPeriodPlan = modPeriodPlans.reduce(
                    (finalPlan, p) => {
                        finalPlan.planAmount += p.planAmount;
                        return finalPlan;
                    },
                    {
                        actgPeriod: modPeriodPlans[0].actgPeriod,
                        planAmount: 0
                    }
                );
            }
            if (modPeriodPlan) {
                modPeriodPlan = Utils.copy(modPeriodPlan);
                const modActgPeriod = modPeriodPlan.actgPeriod;
                const isModAfterEndDate = Utils.isAfter(
                    salesOrder.modificationDate,
                    item.deliveryEndDate
                );
                let daysBeforeMod = Utils.getDayDiff(
                    modActgPeriod.startDate,
                    isModAfterEndDate
                        ? item.deliveryEndDate
                        : salesOrder.modificationDate
                );
                daysBeforeMod += endDateInclusive ? 1 : 0;
                let totalDays = modActgPeriod.numberOfDays();
                const endActgPeriod = Period.toActgPeriod(
                    item.deliveryEndDate,
                    calendarConfig
                );
                if (endActgPeriod.period == modificationPeriod.period) {
                    totalDays = Utils.getDayDiff(
                        modActgPeriod.startDate,
                        item.deliveryEndDate
                    );
                    totalDays += endDateInclusive ? 1 : 0;
                }
                const oneDayPlanAmount = modPeriodPlan.planAmount / totalDays;
                modPeriodPlan.planAmount = oneDayPlanAmount * daysBeforeMod;
            }
            return modPeriodPlan;
        };

        // copy frozen expensePlan from the previousRevenueArrangement
        const copyExpenseFromPreviousArrangement = (
            raItem,
            previousRaItem,
            modificationPeriod
        ) => {
            raItem.expenseItems = Utils.copy(previousRaItem.expenseItems);
            raItem.expenseItems.forEach((expItem) => {
                expItem.deliveryEndDate = salesOrder.modificationDate;
                const frozenPlan =
                    expItem.plan &&
                    expItem.plan.filter((p) => {
                        return Utils.isBefore(
                            p.actgPeriod.period,
                            modificationPeriod.period
                        );
                    });
                if (
                    salesOrder.modificationDate > modificationPeriod.startDate
                ) {
                    const modPeriodPlan = getModificationPeriodPlan(
                        previousRaItem,
                        expItem.plan,
                        modificationPeriod
                    );
                    if (modPeriodPlan) {
                        frozenPlan.push(modPeriodPlan);
                    }
                }
                expItem.plan = frozenPlan;
                expItem.allocatedExpense = expItem.plan.reduce(
                    (total, plan) => total + plan.planAmount,
                    0
                );
            });
        };

        // populate unrecognizeExpenseAmount [expenseAmount - frozenAllocatedExpense] for the expenseItems in the revenueArrangement
        const populateUnrecognizedExpense = () => {
            if (notEmpty(revenueArrangement.expenseItems)) {
                revenueArrangement.expenseItems.forEach((expItem) => {
                    expItem.unrecognizedExpenseAmount = expItem.expenseAmount;
                });
                revenueArrangement.revenueArrangementItem.forEach((raItem) => {
                    if (ItemType.isFrozen(raItem.itemType)) {
                        raItem.expenseItems &&
                            raItem.expenseItems.forEach((expItem) => {
                                const expItemKey =
                                    ExpenseArrangementHelper.getExpenseItemUniqueKey(
                                        expItem
                                    );
                                const raExpItem =
                                    revenueArrangement.expenseItems.find(
                                        (raExpItem) => {
                                            const raExpItemKey =
                                                ExpenseArrangementHelper.getExpenseItemUniqueKey(
                                                    raExpItem
                                                );
                                            if (expItemKey == raExpItemKey) {
                                                return raExpItem;
                                            }
                                        }
                                    );
                                raExpItem.unrecognizedExpenseAmount -=
                                    expItem.allocatedExpense;
                            });
                    }
                });
            }
        };

        // for a frozen item, copy the existing plan from the previous revenueArrangement
        const copyPlanFromPreviousArrangement = (raItem, previousRaItem) => {
            // assume, that the complete previous plan is frozen and modify below if needed
            let frozenPlan = previousRaItem.plan;
            const modificationPeriod = Period.toActgPeriod(
                salesOrder.modificationDate,
                calendarConfig
            );
            switch (previousRaItem.recognitionRule.id) {
                case RecognitionRule.POINT_IN_TIME: {
                    const { deliveryStartDate, deliveryEndDate, ssp, plan } =
                        previousRaItem;
                    const recognizedDate =
                        ssp.recognizedOn === "endDate"
                            ? deliveryEndDate
                            : deliveryStartDate;
                    // modification happens before the recognizedDate, so the frozen plan will be empty
                    if (
                        Utils.isSameOrBefore(
                            salesOrder.modificationDate,
                            recognizedDate
                        )
                    ) {
                        frozenPlan = [
                            {
                                actgPeriod: plan[0].actgPeriod,
                                planAmount: 0
                            }
                        ];
                    }
                    break;
                }
                case RecognitionRule.PROPORTIONAL_PERFORMANCE:
                case RecognitionRule.PERCENT_OF_COMPLETE: {
                    // modification date is between the start and end date of the item, need to recalculate only the frozen part
                    if (
                        isAfter(
                            raItem.salesOrderItem.endDate,
                            salesOrder.modificationDate
                        )
                    ) {
                        if (
                            salesOrder.modificationDate >
                            modificationPeriod.startDate
                        ) {
                            const raItem = Utils.copy(previousRaItem);
                            // modification date is not the first day of the month, recalculate the frozen plan
                            RevenueArrangementUtils.buildPSPlan(
                                raItem,
                                salesOrder,
                                revenueArrangement,
                                null,
                                calendarConfig,
                                orgConfig,
                                orgConfigByKey,
                                true
                            );
                            frozenPlan = raItem.plan.filter((p) => {
                                return Utils.isSameOrBefore(
                                    p.actgPeriod.period,
                                    modificationPeriod.period
                                );
                            });
                        } else {
                            frozenPlan = previousRaItem.plan.filter((p) => {
                                return Utils.isBefore(
                                    p.actgPeriod.period,
                                    modificationPeriod.period
                                );
                            });
                        }
                    }
                    break;
                }
                default: {
                    // consider ratable as the default rule
                    frozenPlan = previousRaItem.plan.filter((p) => {
                        return Utils.isBefore(
                            p.actgPeriod.period,
                            modificationPeriod.period
                        );
                    });
                    if (
                        salesOrder.modificationDate >
                        modificationPeriod.startDate
                    ) {
                        const modPeriodPlan = getModificationPeriodPlan(
                            previousRaItem,
                            previousRaItem.plan,
                            modificationPeriod
                        );
                        if (modPeriodPlan) {
                            frozenPlan.push(modPeriodPlan);
                        }
                    }
                    break;
                }
            }
            raItem.plan = frozenPlan.map((p) => Utils.copy(p));
            raItem.productRevenue = frozenPlan.reduce(
                (total, plan) => total + plan.planAmount,
                0
            );
            if (notEmpty(previousRaItem.expenseItems)) {
                copyExpenseFromPreviousArrangement(
                    raItem,
                    previousRaItem,
                    modificationPeriod
                );
            }
        };

        // merge plans of SPLIT_FROZEN and SPLIT_UNRECOGNIZED to a single plan
        const mergePlan = (plan1, plan2) => {
            const mergedPlan = plan1 || [];
            if (plan2 && plan2.length) {
                mergedPlan.push(...plan2);
            }
            return mergedPlan;
        };

        // merge expense plans of SPLIT_FROZEN and SPLIT_UNRECOGNIZED to a single plan
        const mergeExpenseItems = (activeItem, frozenItem) => {
            const expenseItems = Utils.copy(activeItem.expenseItems) || [];
            frozenItem.expenseItems &&
                frozenItem.expenseItems.forEach((expItem) => {
                    const uniqueKey =
                        ExpenseArrangementHelper.getExpenseItemUniqueKey(
                            expItem
                        );
                    const activeExpItem = expenseItems.find((activeExpItem) => {
                        if (
                            ExpenseArrangementHelper.getExpenseItemUniqueKey(
                                activeExpItem
                            ) === uniqueKey
                        ) {
                            return activeExpItem;
                        }
                    });
                    activeExpItem.expenseAmount = expItem.expenseAmount;
                    activeExpItem.plan = mergePlan(
                        expItem.plan,
                        activeExpItem.plan
                    );
                    activeExpItem.allocatedExpense = activeExpItem.plan.reduce(
                        (total, plan) => total + plan.planAmount,
                        0
                    );
                });
            return expenseItems;
        };

        // get the previous revenueArrangementItem for the sales order item
        const getPreviousRaItem = (salesOrderItem) => {
            const previousRaItems =
                previousRevenueArrangement.revenueArrangementItem.filter(
                    (rai) => rai.salesOrderItemId === salesOrderItem.id
                );
            if (previousRaItems && previousRaItems.length == 2) {
                // If the salesOrderItem already has a prospective modification, we will have two raItems
                // one item of type SPLIT_FROZEN and second of type SPLIT_UNRECOGNIZED
                const frozenItem = previousRaItems.find(
                    (item) => item.itemType == ItemType.SPLIT_FROZEN
                );
                const activeItem = previousRaItems.find(
                    (item) => item.itemType != ItemType.SPLIT_FROZEN
                );
                const plan = mergePlan(frozenItem.plan, activeItem.plan);
                let deliveryLogs;
                if (notEmpty(activeItem.deliveryLogs)) {
                    deliveryLogs = notEmpty(frozenItem.deliveryLogs)
                        ? frozenItem.deliveryLogs
                        : [];
                    deliveryLogs.push(...activeItem.deliveryLogs);
                }
                let expenseItems;
                if (notEmpty(activeItem.expenseItems)) {
                    expenseItems = mergeExpenseItems(activeItem, frozenItem);
                }
                return {
                    plan,
                    ssp: activeItem.ssp,
                    salesOrderItem: activeItem.salesOrderItem,
                    deliveryEndDate: activeItem.deliveryEndDate,
                    productRevenue:
                        frozenItem.productRevenue + activeItem.productRevenue,
                    deliveryLogs,
                    expenseItems,
                    unrecognizedRevenue: activeItem.unrecognizedRevenue,
                    recognitionRule: activeItem.recognitionRule
                };
            }
            return previousRaItems[0];
        };

        const isReprocess =
            previousRevenueArrangement.modificationType ==
                ModificationType.PROSPECTIVE &&
            salesOrder.modificationDate ==
                previousRevenueArrangement.modificationDate;

        revenueArrangement.revenueArrangementItem.forEach((item) => {
            const { startDate, endDate, extendedSalePrice } =
                item.salesOrderItem;
            const currentSoItem = item.salesOrderItem;
            const currentSSP = item.ssp;
            const previousRaItem = getPreviousRaItem(item.salesOrderItem);

            if (Utils.isBefore(startDate, salesOrder.modificationDate)) {
                if (!previousRaItem) {
                    item.itemType = ItemType.FROZEN;
                    if (isAfter(endDate, salesOrder.modificationDate)) {
                        item.itemType = ItemType.SPLIT_UNRECOGNIZED;
                        item.deliveryStartDate = salesOrder.modificationDate;
                        item.unrecognizedRevenue = extendedSalePrice;
                        item.unrecognizedQuantity = currentSoItem.quantity;
                    }
                    return;
                }

                // the sales order item falls under modification, mark the created item as SPLIT_FROZEN
                // and copy the existing plan till the modification date
                item.itemType = ItemType.FROZEN;
                item.recognitionRule = previousRaItem.recognitionRule;
                item.ssp = previousRaItem.ssp;
                item.salesOrderItem = previousRaItem.salesOrderItem;
                item.relativeSalePricePercent = 0.0;
                copyPlanFromPreviousArrangement(item, previousRaItem);

                if (
                    isAfter(endDate, salesOrder.modificationDate) &&
                    !currentSoItem.isDeleted
                ) {
                    // modification date is between the item's start and end date, split the item to create a new unrecognized raItem
                    let unrecognizedRevenue, unrecognizedQuantity;
                    // the unrecognizedRevenue will be the future revenue to be recognized for the item (excluding the previous recognized amount)
                    if (isReprocess) {
                        unrecognizedRevenue =
                            previousRaItem.unrecognizedRevenue;
                        unrecognizedQuantity =
                            previousRaItem.unrecognizedQuantity;
                    } else {
                        const adjustment =
                            extendedSalePrice -
                            previousRaItem.salesOrderItem.extendedSalePrice;
                        unrecognizedRevenue =
                            previousRaItem.productRevenue -
                            item.productRevenue +
                            adjustment;
                        let recognizedQuantity =
                            previousRaItem.salesOrderItem.quantity *
                            (item.productRevenue /
                                previousRaItem.productRevenue);
                        if (!Utils.isNumber(recognizedQuantity)) {
                            recognizedQuantity = 0;
                        }
                        unrecognizedQuantity =
                            currentSoItem.quantity - recognizedQuantity;
                    }
                    if (
                        unrecognizedRevenue == 0 &&
                        currentSSP.recognitionRuleId != RecognitionRule.RATABLE
                    ) {
                        return;
                    }

                    const unrecognizedRaItem =
                        RevenueArrangementUtils.newRevenueArrangementItem(
                            currentSoItem,
                            revenueArrangement.id,
                            ItemType.SPLIT_UNRECOGNIZED
                        );
                    unrecognizedRaItem.isActive = item.isActive;
                    unrecognizedRaItem.deliveryStartDate =
                        salesOrder.modificationDate;
                    unrecognizedRaItem.deliveryEndDate = item.deliveryEndDate;
                    unrecognizedRaItem.ssp = currentSSP;
                    unrecognizedRaItem.unrecognizedRevenue =
                        unrecognizedRevenue;
                    unrecognizedRaItem.unrecognizedQuantity =
                        unrecognizedQuantity;

                    // update the frozen item to split frozen
                    item.itemType = ItemType.SPLIT_FROZEN;
                    item.deliveryEndDate = salesOrder.modificationDate;

                    revenueArrangementItemsToPush.push(unrecognizedRaItem);
                }
            } else {
                // If the item starts after the modificationDate, consider it as an UNRECOGNIZED item
                if (previousRaItem) {
                    if (
                        isReprocess &&
                        previousRaItem.unrecognizedRevenue != null
                    ) {
                        item.itemType = ItemType.SPLIT_UNRECOGNIZED;
                        item.unrecognizedRevenue =
                            previousRaItem.unrecognizedRevenue;
                    } else if (!isReprocess) {
                        item.itemType = ItemType.SPLIT_UNRECOGNIZED;
                        const adjustment =
                            extendedSalePrice -
                            previousRaItem.salesOrderItem.extendedSalePrice;
                        item.unrecognizedRevenue =
                            previousRaItem.productRevenue + adjustment;
                    }
                }
            }
        });

        populateUnrecognizedExpense();

        revenueArrangement.revenueArrangementItem.push(
            ...revenueArrangementItemsToPush
        );

        distributeExcessRevenue(isReprocess);
    }
}
